import { ApiSchema } from "@api-types";
import { differenceInCalendarDays } from "date-fns";
import { odataList } from "services/odataApi";
import { isStatisticallySignificantBinomialTest } from "helpers/statistics";
import { Globals } from "types/globals";
import { ResultsModuleRequests } from "./ResultsModule.requests";
import { ResultsModuleTypes } from "./ResultsModule.types";

export namespace ResultsModuleHelpers {
  const hasTimezone = /(?:Z|[+-]\d{2}:\d{2})$/;

  export const scheduleDateKey = (scheduleDate: string | null | undefined): string =>
    scheduleDate
      ? new Date(hasTimezone.test(scheduleDate) ? scheduleDate : `${scheduleDate}Z`)
          .toISOString()
          .slice(0, 10)
      : "";

  export const deriveWeek = (
    scheduleDate: string | Date | null | undefined,
    launchDate: string | Date | null | undefined,
  ): number =>
    scheduleDate && launchDate
      ? Math.floor(differenceInCalendarDays(new Date(scheduleDate), new Date(launchDate)) / 7)
      : 0;

  export const deriveWeeks = (
    projectSchedules: { surveyRunId?: number | null; scheduleDate?: string | null }[] | null | undefined,
    launchDate: string | Date | null | undefined,
  ): ResultsModuleTypes.Week[] =>
    (projectSchedules ?? [])
      .filter((sr) => sr.surveyRunId !== null && sr.surveyRunId !== undefined)
      .map((sr) => ({
        date: sr.scheduleDate,
        id: sr.surveyRunId!,
        week: deriveWeek(sr.scheduleDate, launchDate),
      }))
      .reduce((p, c) => {
        const existing = p.find((pp) => pp.week === c.week);
        if (existing) {
          existing.ids.push(c.id);
        } else {
          p.push({
            date: new Date(c.date || ""),
            scheduleDateKey: scheduleDateKey(c.date),
            ids: [c.id],
            week: c.week,
          });
        }
        return p;
      }, [] as ResultsModuleTypes.Week[]);

  export const isSharedCampaignOuttake = (
    standardQuestionId: number | null | undefined,
    testOptionIds: number[] | null | undefined,
  ) => standardQuestionId === Globals.StandardQuestions.AdvertisementOuttake && !testOptionIds?.length;

  export const compareSectionForQuestion = (
    q: ResultsModuleRequests.ProjectQuestion,
  ): ResultsModuleTypes.CompareSection => ({
    key: `q-${q.id}`,
    label: q.standardQuestionId
      ? Globals.getStandardQuestionLabel(q.standardQuestionId)
      : q.question.questionTitle,
  });

  export const compareAllowPriceSensitivity = (project: ResultsModuleRequests.Project | undefined) =>
    !!project?.framework.standardQuestions.some((sq) => Globals.isPricingSensitivityQuestion(sq.id)) &&
    (project?.projectPriceSensitivities?.length ?? 0) > 0;

  export const compareDedupeSections = (sections: ResultsModuleTypes.CompareSection[]) =>
    sections.filter((s, i) => sections.findIndex((x) => x.key === s.key) === i);

  export const internalResultsLink = (
    projectId: number,
    path: string,
    urlParams?: Record<string, string | number | (string | number)[] | undefined | null>,
    inheritCurrentParams: boolean = true,
  ): string => {
    const mergedParams = new URLSearchParams();

    if (urlParams) {
      for (const [key, value] of Object.entries(urlParams)) {
        if (value === undefined || value === null) {
          continue;
        }

        if (Array.isArray(value)) {
          const sanitizedArray = value
            .filter((v): v is string | number => v !== undefined && v !== null)
            .map(String);

          if (sanitizedArray.length > 0) {
            mergedParams.set(key, `[${sanitizedArray.join(",")}]`);
          }
        } else {
          mergedParams.set(key, String(value));
        }
      }
    }

    if (inheritCurrentParams) {
      const currentSearchParams = new URLSearchParams(window.location.search);
      for (const [key, value] of currentSearchParams.entries()) {
        if (!mergedParams.has(key)) {
          mergedParams.set(key, value);
        }
      }
    }

    const queryString = mergedParams.toString();
    return `/results/${projectId}/${path}${queryString ? `?${queryString}` : ""}`;
  };

  export const getChartsRoute = (
    projectType: string | null | undefined,
    frameworkId: number | null | undefined,
    firstFollowUpQuestion: { id: number; questionTypeId: number } | null | undefined,
    hasLaunchTestRevampFf: boolean = false,
  ): string => {
    switch (projectType) {
      case "idea":
      case "message":
        if (frameworkId === Globals.Frameworks.PriceSensitivity) return "price-sensitivity";
        if (frameworkId === Globals.Frameworks.MarketReach) return "market-reach";
        return "all";
      case "npd":
        return hasLaunchTestRevampFf ? "launch" : "npd";
      case "question":
        if (firstFollowUpQuestion) {
          const t = firstFollowUpQuestion.questionTypeId === Globals.QuestionType.text ? "text" : "qual";
          return `${t}/${firstFollowUpQuestion.id}`;
        }
        return "";
      default:
        return "";
    }
  };

  export const getKeys = (dictionary: { [key in number]: boolean }) =>
    Object.keys(dictionary).map((a) => dictionary[Number(a)]);

  export const getSubKeys = (dictionary: { [key in number]: { [key in number]: boolean } }) =>
    Object.keys(Object.values(dictionary)).map((a) => dictionary[Number(a)]);

  export const getTargetsReqBuilder = (projectId: number) =>
    odataList("getTargets")
      .select("id", "question", "questionId", "questionOptionId", "systemQuestionOption", "targetMode")
      .filter("projectId", "eq", projectId)
      .expandAndSelect("question", null, "questionTitle", "id")
      .expandAndSelect("questionOption", null, "optionText", "id")
      .expandAndSelect(
        "systemQuestionOption",
        (a) => a.expandAndSelect("systemQuestion", null, "questionLabel"),
        "answerLabel",
      );

  export const getProjectScheduleReqBuilder = (projectId: number) =>
    odataList("getProjectSchedules").filter("projectId", "eq", projectId).orderBy("createdAt");

  export const getPanelTypesReqBuilder = (projectId: number) =>
    odataList("getProjectSchedules")
      .select("id", "regionId", "isCustomerPanel")
      .filter("projectId", "eq", projectId)
      .filter("surveyRunId", "ne", null);

  type ProjectScheduleForRunDate = Pick<
    ApiSchema.ProjectSchedule,
    "scheduleDate" | "surveyRunId" | "regionId"
  >;

  export const getProjectRunDate = (params: {
    launchDate?: Date | string | null;
    createdAt?: Date | string | null;
    projectSchedules?: ProjectScheduleForRunDate[] | null;
    regionId?: number | null;
  }): Date | string | undefined => {
    const baseDate = params.launchDate ?? params.createdAt;

    if (!baseDate) {
      return undefined;
    }

    if (!params.projectSchedules || !params.regionId) {
      return baseDate;
    }

    const completedSchedules = params.projectSchedules.filter(
      (schedule) => schedule.surveyRunId !== null && schedule.regionId === params.regionId,
    );

    if (completedSchedules.length === 0) {
      return baseDate;
    }

    const latestScheduleDate = completedSchedules.reduce<string | undefined>((latest, schedule) => {
      if (!schedule.scheduleDate) {
        return latest;
      }

      if (!latest) {
        return schedule.scheduleDate;
      }

      return new Date(schedule.scheduleDate) > new Date(latest) ? schedule.scheduleDate : latest;
    }, undefined);

    return latestScheduleDate ?? baseDate;
  };

  export const isWaveMode = (
    project?: Pick<ApiSchema.Project, "scheduleStartDate" | "scheduleFrequencyWeeks">,
  ) => {
    if (!project) {
      return false;
    }

    const scheduleStartDate = project.scheduleStartDate ?? null;
    const scheduleFrequencyWeeks = project.scheduleFrequencyWeeks ?? null;

    return scheduleStartDate !== null && scheduleFrequencyWeeks !== null;
  };

  export const SystemQuestions: { [key in Globals.SystemQuestions]: string } = {
    [Globals.SystemQuestions.Generation]: "Generation",
    [Globals.SystemQuestions.HddStructure]: "Household structure",
    [Globals.SystemQuestions.Ethnicity]: "Ethnicity",
    [Globals.SystemQuestions.Region]: "Region",
    [Globals.SystemQuestions.TechAdop]: "Adoption behaviour",
    [Globals.SystemQuestions.Gender]: "Gender",
    [Globals.SystemQuestions.HddIncome]: "Household income",
    [Globals.SystemQuestions.Age]: "Age",
  };

  export const DebounceTimeMs = 1000;

  export const rebaseQualResultsToPopulation = (
    results: ApiSchema.QualResult[] | null | undefined,
    population: number,
  ): ApiSchema.QualResult[] =>
    (results ?? []).map((r) =>
      population ? { ...r, percentage: (r.count / population) * 100, totalCount: population } : r,
    );

  export const launchTestBenchmarkPercentage = (
    selectedBenchmarks: ApiSchema.BenchmarkResult[] | null | undefined,
    standardQuestionId: number,
    alwaysTotalPopulation: boolean = false,
  ) => {
    const benchmark = selectedBenchmarks?.find((b) => b.standardQuestionId === standardQuestionId);
    if (!benchmark) return undefined;
    return (
      (alwaysTotalPopulation || ResultsModuleTypes.ProductLaunchMetrics.includes(standardQuestionId)
        ? benchmark.top2PercentageTotal
        : benchmark.top2Percentage) * 100
    );
  };

  export const deriveLaunchTestBenchmarkDelta = ({
    percentage,
    count,
    target,
    benchmarkMode,
  }: {
    percentage: number;
    count: number;
    target: number | undefined;
    benchmarkMode: "percentage" | "index";
  }): ResultsModuleTypes.LaunchTestBenchmarkDelta => {
    const isStatisticallySignificant =
      !!target &&
      !!percentage &&
      isStatisticallySignificantBinomialTest({
        conceptPercentage: percentage,
        conceptSampleSize: count,
        benchmarkPercentage: target,
      });
    return {
      benchmarkPercentage: target,
      delta:
        !target || !isStatisticallySignificant
          ? undefined
          : benchmarkMode === "percentage"
            ? percentage - target
            : (percentage / target) * 100,
      isStatisticallySignificant,
    };
  };
}
